'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionDetails,
  Card,
  CircularProgress,
} from 'src/components/PulseUI';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import {
  useGetEntityProfileConfigQuery,
  useSaveProfileConfigMutation,
} from 'src/api/redux/profileConfiguration';
import {
  groupWidgetsByCodeSet,
  WidgetRenderer,
} from 'src/components/Profiles/shared';
import type { CodeSetGroup } from 'src/components/Profiles/shared';
import ProfileDetails from 'src/components/Profiles/components/ProfileDetails/ProfileDetails';
import { ProfileAffiliations } from 'src/components/Profiles/components/ProfileAffiliations';
import { ProfilePublications } from 'src/components/Profiles/components/ProfilePublications';
import ProfileEducationDetails from 'src/components/Profiles/components/ProfileEducationDetails';
interface ProfileDrawerConfigProps {
  entityId: string | number;
}

const ProfileDrawerConfig: React.FC<ProfileDrawerConfigProps> = ({
  entityId,
}) => {
  const { data: profileConfig, isLoading } = useGetEntityProfileConfigQuery({
    entityId,
  });

  const [saveConfig] = useSaveProfileConfigMutation();

  const codeSetGroups = useMemo(
    () => groupWidgetsByCodeSet(profileConfig?.widgets ?? []),
    [profileConfig?.widgets]
  );

  const standaloneWidgets = useMemo(
    () => (profileConfig?.widgets ?? []).filter((w) => !w.code_set),
    [profileConfig?.widgets]
  );

  // Local order tracks code set IDs after drag reorder; null = use server order
  const [localOrder, setLocalOrder] = useState<number[] | null>(null);

  const orderedGroups = useMemo(() => {
    if (!localOrder) return codeSetGroups;
    return localOrder
      .map((id) => codeSetGroups.find((g) => g.codeSet.id === id))
      .filter(Boolean) as CodeSetGroup[];
  }, [codeSetGroups, localOrder]);

  // Reset local order when server data changes
  const prevConfigId = React.useRef(profileConfig?.id);
  if (profileConfig?.id !== prevConfigId.current) {
    prevConfigId.current = profileConfig?.id;
    setLocalOrder(null);
  }

  // --- Drag-and-drop state ---
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverIndex(index);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, toIndex: number) => {
      e.preventDefault();
      const fromIndex = draggedIndex;
      setDraggedIndex(null);
      setDragOverIndex(null);

      if (fromIndex === null || fromIndex === toIndex) return;

      const reordered = [...orderedGroups];
      const [movedGroup] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, movedGroup);

      setLocalOrder(reordered.map((g) => g.codeSet.id));

      // Persist: rebuild widget payloads in new group order, then append
      // any standalone widgets (no code_set) to preserve them
      if (profileConfig) {
        const reorderedWidgets = [
          ...reordered.flatMap((group) =>
            group.widgets.map((w) => ({
              code: w.code,
              code_set_id: w.code_set?.id,
              comparison_code_set_id: w.comparison_code_set?.id,
              encounter_id: w.encounter?.id,
              comparison_encounter_id: w.comparison_encounter?.id,
              target_strategy_id: w.target_strategy?.id,
              engage_strategy_id: w.engage_strategy?.id,
            }))
          ),
          ...profileConfig.widgets
            .filter((w) => !w.code_set)
            .map((w) => ({
              code: w.code,
              encounter_id: w.encounter?.id,
              comparison_encounter_id: w.comparison_encounter?.id,
              target_strategy_id: w.target_strategy?.id,
              engage_strategy_id: w.engage_strategy?.id,
            })),
        ];

        saveConfig({
          configId: profileConfig.id,
          entity_type: profileConfig.entity_type,
          widgets: reorderedWidgets,
        });
      }
    },
    [draggedIndex, orderedGroups, profileConfig, saveConfig]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto scrollbar-none"
      data-testid="profile-drawer-config"
      role="list"
      aria-label="Profile configuration"
    >
      <div className="flex flex-col  pb-40">
        {orderedGroups.map((group, index) => (
          <div
            key={group.codeSet.id}
            draggable
            role="listitem"
            aria-label={`${group.codeSet.label} code set, position ${
              index + 1
            } of ${orderedGroups.length}`}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`transition-all ${
              draggedIndex === index ? 'opacity-50 ring-2 ring-sky-300' : ''
            } ${
              dragOverIndex === index && draggedIndex !== index
                ? 'ring-2 ring-sky-500'
                : ''
            }`}
          >
            <Accordion
              variant="plain"
              defaultOpen={false}
              className="w-full bg-white border-b border-neutral-200"
            >
              <AccordionItem
                index={0}
                className="border-none border-b border-neutral-100"
              >
                <AccordionSummary
                  index={0}
                  className="text-sm text-primary"
                  startDecorator={
                    <div
                      className="flex items-center justify-center w-6 h-6 text-tertiary hover:text-neutral-600 cursor-grab"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Drag to reorder"
                    >
                      <Icon icon={faGripVertical} className="w-3 h-3" />
                    </div>
                  }
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold">{group.codeSet.label}</span>
                    {group.codeSet.description && (
                      <span className="text-xs font-normal text-secondary">
                        {group.codeSet.description}
                      </span>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails index={0} className="bg-neutral-050">
                  <div className="flex flex-col gap-3">
                    {group.widgets.map((widget) => (
                      <WidgetRenderer key={widget.id} widget={widget} />
                    ))}
                  </div>
                </AccordionDetails>
              </AccordionItem>
            </Accordion>
          </div>
        ))}
        {/* Provider Details */}
        <Accordion
          variant="plain"
          className="w-full bg-white border-b border-neutral-200"
        >
          <AccordionItem
            index={0}
            className="border-none border-b border-neutral-100"
          >
            <AccordionSummary index={0} className="text-sm text-primary">
              <span className="font-semibold">Provider Details</span>
            </AccordionSummary>
            <AccordionDetails index={0} className="bg-neutral-050">
              <div className="flex flex-col gap-3">
                <ProfileDetails />
                <ProfileAffiliations />
                <ProfilePublications />
                <ProfileEducationDetails />
              </div>
            </AccordionDetails>
          </AccordionItem>
        </Accordion>

        {standaloneWidgets.length > 0 && (
          <div className="flex flex-col gap-3 px-4 py-3">
            {standaloneWidgets.map((widget) => (
              <WidgetRenderer key={widget.id} widget={widget} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileDrawerConfig;
